const GA_MEASUREMENT_ID = "G-3RG4WJKP15";

const EVENT_ALL_CLICK = "all_click";
const EVENT_VIEW_LIST = "view_item_list";
const EVENT_SELECT_ITEM = "select_item";
const EVENT_VIEW_PROMO = "view_promotion";
const EVENT_SELECT_PROMO = "select_promotion";

const PRODUCT_ITEM_CLICK_SELECTOR = ".merca_plp_click";
const PRODUCT_ITEM_VIEW_SELECTOR = ".merca_plp_click";
const PROMO_ITEM_VIEW_SELECTOR = ".merca_promo_view";
const PROMO_ITEM_CLICK_SELECTOR = ".merca_promo_click";

const DEBOUNCE_INTERVAL = 1000;

let observedProductItems = [];
let observedPromoItems = [];
let observerParent = null;
let observerPromoParent = null;

/** Utility: Simple debounce */
function debounce(fn, delay) {
  let timer;
  return function (...args) {
    clearTimeout(timer);
    timer = setTimeout(() => fn.apply(this, args), delay);
  };
}

/** Extract standard click data for GA4 all_click event */
function extractClickData(el) {
  return {
    send_to: GA_MEASUREMENT_ID,
    click_element: el?.tagName?.toLowerCase() || null,
    click_id: el?.id || null,
    click_class: el?.className || null,
    click_target: el?.getAttribute?.("target") || null,
    click_text: el?.innerText?.trim().slice(0, 100) || null,
    click_url: el?.getAttribute?.("href") || el?.getAttribute?.("src") || null,
  };
}

/** Check if click data is meaningful */
function isClickDataEmpty(data) {
  return (
    !data.click_id &&
    (!data.click_class || typeof data.click_class === "object") &&
    !data.click_text &&
    !data.click_url
  );
}

/** Extract product metadata from DOM element */
function extractProductData(el) {
  return {
    item_id: el.getAttribute("data-product-id"),
    item_name: el.getAttribute("data-product-name"),
    // item_brand: el.getAttribute("data-product-vendor"),
    item_list_id:
      el.getAttribute("data-collection-id") ||
      el.getAttribute("data-custom-list-id") ||
      "home",
    item_list_name:
      el.getAttribute("data-product-collection") ||
      el.getAttribute("data-custom-list-name") ||
      "home",
    item_variant: el.getAttribute("data-product-variant"),
    index: el.getAttribute("product-index-number"),
    price: el.getAttribute("data-product-price"),
    quantity: 1,
  };
}

/** Send GA4 event */
function sendGAEvent(eventName, params, retry = 0) {
  const MAX_RETRIES = 50;
  const RETRY_DELAY = 100;

  if (typeof gtag === "function") {
    gtag("event", eventName, {
      send_to: GA_MEASUREMENT_ID,
      ...params,
    });
    return;
  }

  if (retry >= MAX_RETRIES) {
    console.warn("gtag not loaded, event skipped:", eventName);
    return;
  }

  setTimeout(() => {
    sendGAEvent(eventName, params, retry + 1);
  }, RETRY_DELAY);
}

/** IntersectionObserver callback for view_item_list */
function handleProductInView(entries, observer) {
  entries.forEach((entry) => {
    if (entry.isIntersecting) {
      const el = entry.target;
      const productData = extractProductData(el);
      observedProductItems.push(productData);

      if (observedProductItems.length >= 2) {
        sendGAEvent(EVENT_VIEW_LIST, {
          item_list_id: productData.item_list_id,
          item_list_name: productData.item_list_name,
          items: observedProductItems,
        });

        observedProductItems = []; // Reset after send
      }

      // Optionally unobserve after first view
      // observerParent.unobserve(el);
    }
  });
}

/** Setup IntersectionObserver for view_item_list */
function setupIntersectionObserver() {
  const observerOptions = {
    root: null,
    rootMargin: "0px",
    threshold: 0.8,
  };

  observerParent = new IntersectionObserver(
    handleProductInView,
    observerOptions
  );
}

/** Observe any unobserved product items */
function observeNewProductItems() {
  const items = document.querySelectorAll(PRODUCT_ITEM_VIEW_SELECTOR);
  items.forEach((item) => {
    if (!item.dataset.tracked) {
      observerParent.observe(item);
      item.dataset.tracked = "true";
    }
  });
}

/** Setup all_click tracking */
function setupAllClickTracking() {
  document.addEventListener("click", function (event) {
    let element = event.target;
    let clickData = extractClickData(element);

    while (isClickDataEmpty(clickData) && element.parentElement) {
      element = element.parentElement;
      clickData = extractClickData(element);
    }

    sendGAEvent(EVENT_ALL_CLICK, clickData);

    // Debug (optional)
    // console.log("GA4 all_click:", clickData);
  });
}

/** Setup select_item tracking on collection clicks */
function setupCollectionClickTracking() {
  const trackingItems = document.querySelectorAll(PRODUCT_ITEM_CLICK_SELECTOR);

  trackingItems.forEach((item) => {
    if (!item.dataset.boundClick) {
      item.addEventListener("click", function () {
        const productData = extractProductData(this);
        sendGAEvent(EVENT_SELECT_ITEM, {
          item_list_id: productData.item_list_id,
          item_list_name: productData.item_list_name,
          items: [productData],
        });
        // Debug (optional)
        // console.log("GA4 select_item:", productData);
      });
      item.dataset.boundClick = "true";
    }
  });
}

/** Extract promo metadata from DOM element */
function extractPromoData(el) {
  return {
    promotion_id: el.getAttribute("data-promo-id"),
    promotion_name: el.getAttribute("data-promo-name"),
    creative_name: el.getAttribute("data-creative-name"),
    creative_slot: el.getAttribute("promo-index-number"),
  };
}

/** Observe any unobserved promo items */
function observeNewPromoItems() {
  const items = document.querySelectorAll(PROMO_ITEM_VIEW_SELECTOR);
  items.forEach((item) => {
    if (!item.dataset.tracked) {
      observerPromoParent.observe(item);
      item.dataset.tracked = "true";
    }
  });
}

/** IntersectionObserver callback for view_promotion */
function handlePromoInView(entries, observer) {
  entries.forEach((entry) => {
    if (entry.isIntersecting) {
      const el = entry.target;
      const promoData = extractPromoData(el);

      sendGAEvent(EVENT_VIEW_PROMO, {
        items: [promoData],
      });

      // Optionally unobserve after first view
      // observerPromoParent.unobserve(el);
    }
  });
}

/** Setup IntersectionObserver for view_promotion */
function setupPromoIntersectionObserver() {
  const observerOptions = {
    root: null,
    rootMargin: "0px",
    threshold: 0.8,
  };

  observerPromoParent = new IntersectionObserver(
    handlePromoInView,
    observerOptions
  );
}

/** Setup select_item tracking on collection clicks */
function setupPromoClickTracking() {
  const trackingItems = document.querySelectorAll(PROMO_ITEM_CLICK_SELECTOR);

  trackingItems.forEach((item) => {
    if (!item.dataset.boundClick) {
      item.addEventListener("click", function () {
        const promoData = extractPromoData(this);
        sendGAEvent(EVENT_SELECT_PROMO, promoData);
        // Debug (optional)
        // console.log("GA4 select_promotion:", promoData);
      });
      item.dataset.boundClick = "true";
    }
  });
}

/** Observe dynamic changes and update observers/bindings */
function observeDOMChanges() {
  const debouncedUpdate = debounce(() => {
    observeNewProductItems();
    observeNewPromoItems();
    setupCollectionClickTracking();
    setupPromoClickTracking();
  }, DEBOUNCE_INTERVAL);

  const mo = new MutationObserver(debouncedUpdate);
  mo.observe(document.body, {
    childList: true,
    subtree: true,
  });
}

/** Initialize tracking */
document.addEventListener("DOMContentLoaded", function () {
  setupIntersectionObserver();
  setupPromoIntersectionObserver();
  observeNewProductItems(); 
  observeNewPromoItems(); 
  setupAllClickTracking();
  setupCollectionClickTracking(); 
  setupPromoClickTracking();
  observeDOMChanges(); // for dynamic content
});
